import _ from 'lodash';

/**
 * Returns a list of campaigns grouped by type
 *
 * @param {Array} campaigns array of campaigns
 * @returns {Object} list of campaigns grouped by type
 */
export const getCampaignList = (campaigns) =>
  campaigns.reduce(
    (list, campaign) => {
      const type = campaign.type.toLowerCase();
      if (
        type === 'season' ||
        type === 'boost' ||
        type === 'edit' ||
        type === 'shop'
      ) {
        return {
          ...list,
          [type]: [...list[type], campaign],
        };
      }
      return list;
    },
    { season: [], boost: [], edit: [], shop: [] }
  );

/**
 * Returns the default campaign ID
 *
 * @param {Array} campaigns array of campaigns
 * @returns {string} default campaign ID
 */
export const getDefaultCampaign = (campaigns) => {
  if (!campaigns.length) return '';
  const def = campaigns.find((campaign) => campaign.default);
  return def && def.id && def.id.toString();
};

/**
 * Returns an array of modified campaign IDs
 *
 * @param {number} selectedSeason selected Season campaign ID
 * @param {number} selectedBoost selected Boost campaign ID
 * @param {number} selectedEdit selected Edit campaign ID
 * @param {number} selectedShop selected Shop campaign ID
 * @param {number} defaultSeason default Season campaign ID
 * @param {number} defaultBoost default Boost campaign ID
 * @param {number} defaultEdit default Edit campaign ID
 * @param {number} defaultShop default Shop campaign ID
 * @returns {Array} array of modified campaign IDs
 */
export const getDiff = (
  selectedSeason,
  selectedBoost,
  selectedEdit,
  selectedShop,
  defaultSeason,
  defaultBoost,
  defaultEdit,
  defaultShop
) => {
  const diff = [];
  if (selectedSeason !== defaultSeason) diff.push(selectedSeason);
  if (selectedBoost !== defaultBoost) diff.push(selectedBoost);
  if (selectedEdit !== defaultEdit) diff.push(selectedEdit);
  if (selectedShop !== defaultShop) diff.push(selectedShop);
  return diff;
};

/**
 * Returns a particular campaign field
 *
 * @param {Array} campaigns array of campaigns
 * @param {string} campaignId campaign ID
 * @param {string} field field name
 * @returns {string} field value
 */
const getCampaignData = (campaigns, campaignId, field) => {
  const campaign = campaigns.find((item) => item.id.toString() === campaignId);
  const data = campaign[field];
  if (field === 'type') {
    return data === 'SEASON' ? 'Add-Ons' : _.startCase(_.toLower(data));
  }
  return data;
};

/**
 * Returns a success message based on the number of campaigns
 *
 * @param {Array} campaigns array of campaigns
 * @param {Array} diff array of campaign IDs
 * @returns {string} message
 */
export const getSuccessToastTitle = (campaigns, diff) => {
  switch (diff.length) {
    case 1:
      return `Campaign ${getCampaignData(
        campaigns,
        diff[0],
        'name'
      )} is set as the default campaign.`;
    case 2:
      return `Campaign ${getCampaignData(
        campaigns,
        diff[0],
        'name'
      )} and ${getCampaignData(
        campaigns,
        diff[1],
        'name'
      )} are set as the default campaigns.`;
    case 3:
      return `Campaign ${getCampaignData(
        campaigns,
        diff[0],
        'name'
      )}, ${getCampaignData(campaigns, diff[1], 'name')},
      and ${getCampaignData(
        campaigns,
        diff[2],
        'name'
      )} are set as the default campaigns.`;
    case 4:
      return `Campaign ${getCampaignData(
        campaigns,
        diff[0],
        'name'
      )}, ${getCampaignData(campaigns, diff[1], 'name')},
      ${getCampaignData(campaigns, diff[2], 'name')},
      and ${getCampaignData(
        campaigns,
        diff[3],
        'name'
      )} are set as the default campaigns.`;
    default:
      return 'Campaign is set as the default campaign';
  }
};

/**
 * Returns an error message based on the number of campaigns
 *
 * @param {Array} campaigns array of campaigns
 * @param {Array} diff array of campaign IDs
 * @returns {string} message
 */
export const getErrorToastTitle = (campaigns, diff) => {
  switch (diff.length) {
    case 1:
      return `The default ${getCampaignData(
        campaigns,
        diff[0],
        'type'
      )} campaign was not updated. Please try again.`;
    case 2:
      return `The default campaigns for ${getCampaignData(
        campaigns,
        diff[0],
        'type'
      )} and ${getCampaignData(
        campaigns,
        diff[1],
        'type'
      )} were not updated. Please try again. `;
    case 3:
      return `The default campaigns for ${getCampaignData(
        campaigns,
        diff[0],
        'type'
      )}, ${getCampaignData(campaigns, diff[1], 'type')},
      and ${getCampaignData(
        campaigns,
        diff[2],
        'type'
      )} were not updated. Please try again. `;
    case 4:
      return `The default campaigns for ${getCampaignData(
        campaigns,
        diff[0],
        'type'
      )}, ${getCampaignData(campaigns, diff[1], 'type')},
      ${getCampaignData(campaigns, diff[2], 'type')},
      and ${getCampaignData(
        campaigns,
        diff[3],
        'type'
      )} were not updated. Please try again. `;
    default:
      return 'The default campaigns were not updated. Please try again.';
  }
};
